import React, { useCallback, useMemo } from 'react';

import { ia } from 'lib/helpers/utility';

import AGInfiniteTable from 'components/shared/AGInfiniteTable/AGInfiniteTable';
import { GRID_OPTIONS } from 'components/shared/AgTablePage/lib/constants';
import Icon from 'components/shared/Icon/Icon';

const TabTable = ({
  tab,
  alwaysShowDetails,
  setDetails,
  setSelected,
  selected,
  setFetchedData,
  prefix
}) => {
  const selectionType = tab?.selectionType || 'multiple';
  const effectiveSelection = tab?.table?.options?.rowSelection ?? selectionType;
  const hasSelection = effectiveSelection === 'single' || effectiveSelection === 'multiple';
  const gridOptions = useMemo(
    () => ({
      ...GRID_OPTIONS,
      rowGroupPanelShow: 'never',
      rowSelection: hasSelection ? effectiveSelection : null,
      ...(tab?.table?.options || {}),
      suppressDragLeaveHidesColumns: true,
      suppressRowClickSelection: true,
      suppressColumnMove: true,
      suppressMovableColumns: true,
      suppressColumnVirtualisation: false,
      suppressColumnSetPanel: true
    }),
    [tab?.selectionType, tab?.table?.options?.rowSelection]
  );

  const setSelectedForTab = useCallback(
    (rows) => {
      if (!tab?.name) return;
      setSelected((old) => ({ ...old, [tab.name]: rows }));
    },
    [tab.name]
  );

  const preSelectedIds = useMemo(
    () =>
      ia(tab?.table?.preSelectedIds)
        ? tab?.table?.preSelectedIds
        : ia(selected[tab.name])
          ? selected[tab.name]?.map(({ data }) => data?.id)
          : null,
    [tab?.table?.preSelectedIds, selected[tab.name]]
  );

  if (!tab?.table) return null;

  const checkboxCol = hasSelection
    ? {
        field: 'checkbox',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        resizable: false,
        sortable: false,
        suppressMovable: true,
        lockPosition: 'left',
        suppressMenu: true,
        suppressHeaderMenuButton: true,
        menuTabs: [],
        lockPinned: true,
        lockVisible: true,
        headerCheckboxSelection: effectiveSelection !== 'single',
        checkboxSelection: true,
        cellClass: 'no-border checkbox-col',
        headerClass: 'checkbox-col'
      }
    : null;

  const defaultColumns = [
    ...(checkboxCol ? [checkboxCol] : []),
    ...tab.table.defaultColumns
      .filter((col) => (tab.table.showColumns ? tab.table.showColumns?.includes(col.field) : true))
      .map((col) => ({
        ...col,
        flex: 1,
        // Strip any checkbox selection that callers may have set on data columns
        checkboxSelection: false,
        headerCheckboxSelection: false,
        suppressMenu: true,
        suppressHeaderMenuButton: true,
        menuTabs: [],
        suppressMovable: true,
        lockPinned: true,
        lockVisible: true
      })),
    ...((tab.details?.component && !alwaysShowDetails) || tab?.table?.customActions
      ? [
          {
            field: 'actions',
            headerName: tab.details?.component ? 'Preview' : 'Actions',
            maxWidth: 75,
            minWidth: 75,
            sortable: false,
            suppressMenu: true,
            cellRenderer: (props) => {
              const shouldShowDetails =
                typeof tab?.table?.showDetails === 'function'
                  ? tab.table.showDetails(props.data)
                  : tab?.table?.showDetails !== false;

              if (!shouldShowDetails) return null;

              if (tab?.table?.customActions) {
                return tab.table.customActions({ ...props, setDetails });
              }

              return (
                <div
                  className="flex h-full w-full items-center justify-center gap-2 transition-all hover:scale-110 active:scale-90"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetails(props?.data);
                  }}>
                  <Icon icon="document-preview-mono" className="cursor-pointer" />
                </div>
              );
            }
          }
        ]
      : [])
  ];

  const defaultFilters = Object.keys(tab.table.defaultFilters || {})
    .filter((key) => (tab.table.showFilters ? tab.table.showFilters?.includes(key) : true))
    .reduce((obj, key) => {
      obj[key] = tab.table.defaultFilters[key];
      return obj;
    }, {});

  return (
    <AGInfiniteTable
      key={tab.table.name}
      {...tab.table}
      setSelectedRows={setSelectedForTab}
      selectedRows={selected[tab.name]}
      disableRowGrouping
      setFetchedData={setFetchedData}
      pagination={false}
      skeletonSize={100}
      modalView
      noDisplayButton
      preSelectedIds={preSelectedIds}
      defaultColumns={defaultColumns}
      defaultFilters={defaultFilters}
      gridOptions={gridOptions}
      customClassName={
        [
          selectionType === 'single' || effectiveSelection === 'single'
            ? 'ag-single-selection'
            : undefined,
          '[&_.ag-root-wrapper]:!rounded-t-none',
          tab?.table?.customClassName
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
      paramPrefix={prefix ? `${tab?.name}_${prefix}` : tab?.name}
    />
  );
};

export default TabTable;
